import * as yup from 'yup';
import { useExperiment } from '@web/experiments';
import {
  nullableImageFromCMSSchema,
  requiredImageFromCMSSchema,
  richTextFromCMSSchema,
} from '../../cms';
import { full_ingredients_modal } from '../../cms/builders';
import dogInBoxEmpty from '../../images/dog-in-box-empty.png';
import {
  planLoaderMessages as defaultPlanLoaderMessages,
  planLoaderButtonText as defaultPlanLoaderButtonText,
} from '../constants';

const itemSchema = yup.object({
  title: yup.string().required(),
  label: yup.string().required(),
  benefit: yup.string().optional(),
  callout: yup.string().optional(),
  value: yup.string().required(),
  description: richTextFromCMSSchema,
  img: requiredImageFromCMSSchema,
  unavailableText: yup.string(),
  priceSymbol: yup.string().optional(),
  showMoreDetails: yup.bool(),
  pricingInfo: yup.object({
    price: yup.string(),
    discount: yup.string(),
  }),
  level: yup.string(),
  hidden: yup.bool(),
});

const planLoaderMessageSchema = yup.object({
  orientation: yup.string(),
  bgColor: yup.string(),
  headline: yup.string().required(),
  text: yup.string(),
  image: nullableImageFromCMSSchema,
});

export const planSelectionSchema = yup
  .object({
    planSelectionInfo: yup
      .object({
        headline: yup.string().required(),
        subheadline: yup.string().required(),
        summaryHeadline: yup.string().required(),
        image: requiredImageFromCMSSchema,
        items: yup
          .array(
            itemSchema
              .from('display_name', 'title')
              .from('slug', 'value')
              .from('image', 'img')
              .from('unavailable_text', 'unavailableText')
              .from('show_more_details', 'showMoreDetails')
              .from('hidden', 'hidden'),
          )
          .required(),
        collapsedItems: yup
          .array(
            itemSchema
              .from('display_name', 'title')
              .from('slug', 'value')
              .from('image', 'img')
              .from('unavailable_text', 'unavailableText')
              .from('show_more_details', 'showMoreDetails')
              .from('hidden', 'hidden'),
          )
          .required(),
        mobileCtaText: yup.string().required(),
        desktopCtaText: yup.string().required(),
        modals: yup.array(full_ingredients_modal.schema).required(),
        notSelectableText: yup.string(),
      })
      .from('starter_plan_summary_headline', 'summaryHeadline')
      .from('starter_plan_icon', 'image')
      .from('desktop_cta_text', 'desktopCtaText')
      .from('mobile_cta_text', 'mobileCtaText')
      .from('not_selectable_text', 'notSelectableText'),
    noRecipesInfo: yup.object({}).required(),
    body: yup.array(yup.mixed()),
    planLoaderHeader: yup.string(),
    planLoaderMessages: yup.array(planLoaderMessageSchema).required(),
    planLoaderButtonText: yup.string(),
  })
  .transform(({ body, ...value }) => {
    let crunchingNumbersContent = value.crunchingNumbersContent?.[0];
    let { value: crunchingNumbersExperimentValue } = useExperiment(
      crunchingNumbersContent?.test_name ?? null,
    );
    crunchingNumbersExperimentValue =
      crunchingNumbersExperimentValue || 'control';
    if (crunchingNumbersContent?.component === 'flagship_ab_test') {
      const experimentVariation = crunchingNumbersContent?.variants.find(
        variant => variant.variant_name === crunchingNumbersExperimentValue,
      );
      crunchingNumbersContent = experimentVariation?.blocks?.[0] ?? [];
    }
    let { value: experimentValue } = useExperiment(
      value.recipe_selector?.[0]?.test_name ?? null,
    );
    experimentValue = experimentValue || 'control';
    let productSelectorCards = value.recipe_selector;
    if (productSelectorCards?.[0].component === 'flagship_ab_test') {
      const experimentVariation = productSelectorCards[0].variants.find(
        variant => variant.variant_name === experimentValue,
      );
      productSelectorCards = experimentVariation?.blocks ?? [];
    }
    return {
      // TODO(James) integrate the rest of this with the CMS
      planSelectionInfo: {
        ...value,
        showMoreDetails: value.show_more_details,
        ctaText: 'Continue',
        items: productSelectorCards
          .filter(item => !item.hidden)
          .map(recipe => ({
            ...recipe,
            priceSymbol: recipe.price_symbol,
            pricingInfo: {
              price: recipe.price,
              discount: recipe.discount,
            },
          })),
        collapsedItems: productSelectorCards
          .filter(item => item.hidden)
          .map(recipe => ({
            ...recipe,
            priceSymbol: recipe.price_symbol,
            pricingInfo: {
              price: recipe.price,
              discount: recipe.discount,
            },
          })),
      },
      noRecipesInfo: {
        headline: 'Doggone it!',
        subheadline:
          "We're unable to recommend a recipe for {{pupName}} at this time due to one or more allergies.",
        messagePreText: "You told us that {{pupName}} can't have:",
        messagePostText:
          "Double check this is accurate and adjust {{pupName}}'s allergy profile to see available recipes",
        buttonText: "Edit {{pupName}}'s allergies",
        image: {
          src: dogInBoxEmpty.src,
          alt: 'dog in empty box',
        },
      },
      planLoaderHeader: crunchingNumbersContent?.headline,
      planLoaderMessages:
        crunchingNumbersContent?.messages || defaultPlanLoaderMessages,
      planLoaderButtonText:
        crunchingNumbersContent?.buttonText || defaultPlanLoaderButtonText,
      body,
    };
  });

export const recommendedPlanSchema = yup
  .object({
    noRecipesInfo: yup.object({}).required(),
    planLoaderHeader: yup.string(),
    planLoaderMessages: yup.array(planLoaderMessageSchema).required(),
    planLoaderButtonText: yup.string(),
  })
  .transform(({ body, ...value }) => {
    let crunchingNumbersContent = value.crunchingNumbersContent?.[0];
    let { value: experimentValue } = useExperiment(
      crunchingNumbersContent?.test_name ?? null,
    );
    experimentValue = experimentValue || 'control';
    if (crunchingNumbersContent?.component === 'flagship_ab_test') {
      const experimentVariation = crunchingNumbersContent?.variants.find(
        variant => variant.variant_name === experimentValue,
      );
      crunchingNumbersContent = experimentVariation?.blocks?.[0] ?? [];
    }

    return {
      noRecipesInfo: {
        headline: 'Doggone it!',
        subheadline:
          "We're unable to recommend a recipe for {{pupName}} at this time due to one or more allergies.",
        messagePreText: "You told us that {{pupName}} can't have:",
        messagePostText:
          "Double check this is accurate and adjust {{pupName}}'s allergy profile to see available recipes",
        buttonText: "Edit {{pupName}}'s allergies",
        image: {
          src: dogInBoxEmpty.src,
          alt: 'dog in empty box',
        },
      },
      planLoaderHeader: crunchingNumbersContent?.headline,
      planLoaderMessages:
        crunchingNumbersContent?.messages || defaultPlanLoaderMessages,
      planLoaderButtonText:
        crunchingNumbersContent?.buttonText || defaultPlanLoaderButtonText,
    };
  });
